<script lang="ts" setup>
import { ref, watch, computed, useAttrs, nextTick } from 'vue';
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';

interface Tag {
    label: string;
    value: string;
    bgColor?: string;
    textColor?: string;
}

interface Attrs {
    placeholder?: string;
    options?: Tag[];
}

const props = defineProps<{
    modelValue?: Tag[] | null;
    placeholder?: string;
    defaultOptions?: Tag[];
}>();

const emits = defineEmits<{
    (e: 'update:modelValue', value: Tag[]): void;
    (e: 'change', value: Tag[]): void;
    (e: 'select', value: Tag): void;
    (e: 'remove', value: Tag): void;
    (e: 'search-change', value: string): void;
    (e: 'create', value: Tag): void;
}>();

const attrs = useAttrs() as Attrs;
const inputValue = ref<Tag[]>([]);
const localOptions = ref<Tag[]>([]);
const searchQuery = ref('');

const placeholder = computed(() => {
    return props.placeholder || (attrs.placeholder as string) || 'Input Here';
});

const filteredOptions = computed(() => {
    const externalOptions = (attrs.options || []) as Tag[];
    // Prepend caller-configured default options (e.g. member_price for pricing) missing from options
    const defaults = (props.defaultOptions || []).filter(d => !externalOptions.some(opt => opt.label === d.label));
    const allOptions = [...defaults, ...externalOptions, ...localOptions.value].filter(option =>
        !inputValue.value.some(selected => selected.label === option.label)
    );

    if (!searchQuery.value) return allOptions;
    const query = searchQuery.value.toLowerCase().trim();
    const filtered = allOptions.filter(option => option.label.toLowerCase().includes(query));
    return Array.from(new Map(filtered.map(item => [item.label, item])).values());
});

const handleSearchChange = (query: string) => {
    searchQuery.value = query;
    emits('search-change', query);
};

const handleCreateTag = (searchQuery: string) => {
    if (!searchQuery?.trim()) return;

    const newTag: Tag = {
        label: searchQuery.trim(),
        value: searchQuery.trim(),
        bgColor: '#e5e7eb',
        textColor: '#374151'
    };

    const existsInSelected = inputValue.value.some(tag => tag.label === newTag.label);
    const existsInOptions = filteredOptions.value.some(tag => tag.label === newTag.label);

    if (!existsInSelected && !existsInOptions) {
        localOptions.value = [...localOptions.value, newTag];
        handleChange([...inputValue.value, newTag]);
        emits('create', newTag);
    }
};

const handleRemove = (option: Tag) => {
    if (!option) return;

    const newValue = inputValue.value.filter(tag => tag.label !== option.label);

    localOptions.value = localOptions.value.filter(tag => tag.label !== option.label);

    handleChange(newValue);
    emits('remove', option);
};

const handleChange = (value: Tag[]) => {
    const newValue = value || [];
    inputValue.value = newValue;

    if (newValue.length === 0) {
        localOptions.value = [];
    }

    emits('update:modelValue', newValue);
    emits('change', newValue);
};

watch(() => props.modelValue, (newVal) => {
    if (newVal) {
        inputValue.value = [...newVal];
    } else {
        inputValue.value = [];
        localOptions.value = [];
    }
}, { immediate: true, deep: true });

const handleOpen = () => {
    nextTick(() => {
        const dropdown = document.querySelector('.multiselect-dropdown') as HTMLElement;
        // Not dead code: fixes the dropdown margin bug
        if (dropdown) dropdown.style.marginTop = '0';
    });
};
</script>

<template>
    <div class="tag-select-container">
        <Multiselect
            v-model="inputValue"
            mode="tags"
            :close-on-select="false"
            :close-on-deselect="false"
            :clear-on-select="false"
            :preserve-search="true"
            :searchable="true"
            :create-option="true"
            :append-new-option="false"
            :options="filteredOptions"
            :placeholder="placeholder"
            track-by="label"
            label="label"
            @change="handleChange"
            @tag="handleCreateTag"
            @remove="handleRemove"
            @open="handleOpen"
            @search-change="handleSearchChange"
            class="multiselect-widget"
        />
    </div>
</template>

<style lang="scss">
.tag-select-container {
    position: relative;
    width: 100%;
}

.multiselect {
    min-height: 50px;
    border-radius: 10px;
    border: 1px solid #a8a8a8;
    width: 100%;
    padding: 0 8px;
    background-color: #fff;
    transition: all 0.2s;

    &:hover {
        border-color: #57589B;
    }

    &.is-active {
        border-radius: 10px;
        border-color: #57589B;
        box-shadow: none;
    }

    &-wrapper {
        padding: 4px 0;
        min-height: 40px;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
    }

    &-tags {
        margin: 0;
        padding: 0;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }

    &-tag {
        background: #57589B;
        color: #ffffff;
        border-radius: 10px;
        padding: 2px 8px;
        margin: 2px 4px;
        font-size: 14px;
        display: flex;
        align-items: center;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &-remove {
            margin-left: 4px;
            padding: 1px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: #ffffff;

            &:hover {
                color: #ff4444;
                background-color: rgba(255, 255, 255, 0.2);
            }
        }
    }

    &-placeholder {
        color: #909399;
        font-size: 14px;
        padding: 0 8px;
        line-height: 40px;
    }

    &-search {
        margin: 0;
        width: 100%;
        min-width: 60px;
        padding: 8px;

        input {
            background: transparent;
            border: none;
            outline: none;
            padding: 0;
            margin: 0;
            width: 100%;
            color: #374151;
            font-size: 14px;

            &::placeholder {
                color: #909399;
            }
        }
    }

    &-dropdown {
        background: #ffffff;
        border: 1px solid #57589B;
        border-radius: 10px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        z-index: 999;
        max-height: 300px;
        overflow-y: auto;

        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background-color: #9ca3af;
            border-radius: 3px;
        }
    }

    &-options {
        padding: 4px 0;
    }

    &-option {
        padding: 8px 12px;
        color: #374151;
        cursor: pointer;
        display: block !important;

        &:hover {
            background: #f3f4f6;
        }

        &.is-selected {
            background: #e5e7eb;
            color: #374151;
            display: none !important;
        }

        &.is-pointed {
            background: #f3f4f6;
        }
    }

    &-no-options,
    &-no-results {
        padding: 8px 12px;
        color: #9ca3af;
    }
}
</style>
